import * as React from 'react';
import { useParams } from 'react-router-dom';
import { ActionPlanView, SurveyActionPlanTopic } from '../../amorg-common';
import { getActionPlanFromLinkId } from '../../api/actionPlanClientApi';
import { Card } from '../../designSystem/Card';
import { colors } from '../../designSystem/colors/color';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { ListRow } from '../../designSystem/compositeComponents/ListRow';
import { StaticTextField } from '../../designSystem/formField/StaticTextField';
import { iconName, Icon } from '../../designSystem/icons/Icons';
import { Layout2 } from '../../designSystem/layouts/Layout2';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { MessageCard } from '../../designSystem/Message';
import { P } from '../../designSystem/typography/Body';
import { H1, H2, H4 } from '../../designSystem/typography/Heading';
import { PublicPageProvider } from '../_contexts/PublicPageContext';
import { SurveyTopicInfoPanel } from './components/SurveyTopicInfoPanel';
import { ReadOnlySegmentPanel } from '../../features/surveys/segmentation/ReadOnlySegmentPanel';
import { ArticleVersionFields } from '../_components/articles/ArticleVersionFields';

type PageState = {
    publicActionPlan: ActionPlanView,
    loading: boolean;
};
export default function ActionPlanPublicPage (): JSX.Element 
{
    const params = useParams<{ siteId: string, linkId: string; }>();

    const [state, setState] = React.useState<PageState>({
        publicActionPlan: null,
        loading: true
    });

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            const publicActionPlan = await getActionPlanFromLinkId(params.siteId, params.linkId);
            const newState = {
                publicActionPlan,
                loading: false
            };
            setState(newState);
        };
        getData();
    }, []);

    const segments = state.publicActionPlan?.type === 'WPA'
        ? state.publicActionPlan.customData.segmentFilter
        : [];

    return (
        <PublicPageProvider>
            <Layout2
                header={
                    <div>
                        {state.loading &&
                            <LoadingPageOverlay loading={state.loading} />
                        }

                        {state.publicActionPlan !== null &&
                            <H1 color="gray">Handleplan</H1>
                        }
                    </div>
                }

                content={
                    <div style={{marginTop: '16px', marginBottom: '16px' }}>
                        {!state.loading && state.publicActionPlan === null &&
                            <MessageCard variant="error">Det valgte link er ugyldigt, eller ikke længere tilgængeligt. Kontakt din arbejdsmiljøgruppe.</MessageCard>
                        }

                        {!state.loading && state.publicActionPlan !== null &&
                            <Flex flexDirection='column' gap={32}>
                                <H2>{state.publicActionPlan?.title || ''}</H2>

                                <StaticTextField label="Beskrivelse"
                                    staticText={state.publicActionPlan?.description || 'Ingen beskrivelse'} />

                                <StaticTextField label="Ansvarlig for handleplanen"
                                    staticText={state.publicActionPlan?.owner?.fullName || 'Ingen valgt'} />
                                
                                {state.publicActionPlan?.type === 'WPA' && state.publicActionPlan.customData.segmentFilter.length > 0 &&
                                    <div>
                                        <H2>Segmenter</H2>
                                        <ReadOnlySegmentPanel segmentLists={segments} lockedView='pills' />
                                    </div>
                                }

                                {state.publicActionPlan?.type === 'Article' &&
                                    <ArticleVersionFields {...state.publicActionPlan.articleData} 
                                        articleId={state.publicActionPlan.customData.articleId} 
                                        importance={state.publicActionPlan.articleData.importance}
                                        disableEdit />
                                }

                                <ActionPlanPublicView actionPlan={state.publicActionPlan} />
                            </Flex>
                        }
                    </div>
                }
            />
        </PublicPageProvider>
    );
}

type ActionPlanPublicViewProps =
    {
        actionPlan: ActionPlanView;
    };
function ActionPlanPublicView (props: ActionPlanPublicViewProps): JSX.Element
{
    return (
        <>

            <H2>Handleplanens emner</H2>

            {props.actionPlan.topics.length === 0 &&
                <i>Ingen emner oprettet</i>
            }
            {props.actionPlan.topics.length > 0 &&
                <Flex flexDirection="column" gap={24}>
                    {props.actionPlan.topics.map(t =>
                    {
                        return (
                            <PublicActionPlanTopicView
                                key={t.id}
                                topic={t} />
                        );
                    })}
                </Flex>
            }
        </>
    );
}

type PublicActionPlanTopicViewProps =
    {
        topic: SurveyActionPlanTopic;
    };
function PublicActionPlanTopicView (props: PublicActionPlanTopicViewProps): JSX.Element 
{
    return (
        <Flex flexDirection="column" gap={2}>
            <ListRow>
                <P>{props.topic.description}</P>
            </ListRow>
            <Card variant="outlined">
                <Flex flexDirection="column" gap={16}>
                    {props.topic.surveyTopic &&
                        <Card variant="outlined">
                            <SurveyTopicInfoPanel surveyTopic={props.topic.surveyTopic} />
                        </Card>
                    }
                    <div>
                        {props.topic.actionPoints.length === 0 &&
                            <>
                                <i>Ingen handlepunkter oprettet</i>
                            </>
                        }

                        {props.topic.actionPoints.length > 0 &&
                            <>
                                <H4>Handlepunkter</H4>
                                {props.topic.actionPoints.map((ap) =>
                                {
                                    let icon: { name: iconName, color: string; } = { name: 'navigation_clock', color: colors.icon.passive };
                                    if (ap.done)
                                        icon = { name: 'status_ok', color: colors.icon.success };
                                    else if (!ap.done && ap.deadline && new Date(ap.deadline) < new Date())
                                        icon = { name: 'status_error', color: colors.icon.error };

                                    return (
                                        <ListRow key={ap.id} styleVariant="neutral" sizeVariant="slim" level="level1" >
                                            <Flex justifyContent="space-between" gap={32} alignItems='center'>
                                                <Flex alignItems="center" gap={8} style={{ flexGrow: 1 }}>

                                                    <Icon name={icon.name} color={icon.color} />

                                                    <P>{ap.description}</P>
                                                </Flex>



                                                <Flex flexDirection="column" justifyContent="center" gap={0}>
                                                    <Flex alignItems='center' gap={8} style={{ width: '240px' }}>
                                                        <Icon name="interface_user_user" />
                                                        <div style={{ flexGrow: 1 }}>
                                                            { ap.assignedUser === null
                                                                ? <span style={{ color: colors.text.inactive }}>Ingen ejer</span>
                                                                : <span>{ap.assignedUser.fullName}</span>
                                                            }
                                                        </div>
                                                    </Flex>

                                                    <Flex alignItems='center' gap={8}>
                                                        <Icon name="navigation_calendar" />

                                                        <div style={{ width: '170px' }}>
                                                            {ap.deadline === null 
                                                                ? <span style={{ color: colors.text.inactive }}>Ingen deadline</span>
                                                                : <span>{ap.deadline.toLocaleDateString()}</span>
                                                            }
                                                        </div>
                                                    </Flex>

                                                </Flex>

                                                <Flex flexDirection="column" style={{width:'100px', flexGrow:0}}>
                                                    {ap.done &&
                                                        <i>Opgaven er gennemført</i>
                                                    }
                                                </Flex>

                                            </Flex>
                                        </ListRow>
                                    );
                                })}

                            </>
                        }
                    </div>
                </Flex>
            </Card >
        </Flex >
    );
}